/**
 * Sticky Add-to-Cart Bar
 * Mobile-only sticky bar that appears when the main buy box CTA scrolls out of view.
 * Delegates add-to-cart to the existing ProductFormComponent on the page.
 */

import { Component } from "./component.js";

class GRStickyAddToCart extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLSelectElement | null} */
  #frequencySelect = null;

  /** @type {any} */
  #variant = null;

  /** Whether a product has been added to cart — once true, sticky ATC stays hidden */
  #productAdded = false;

  /** @type {number | null} Fetched bundle variant price in cents (nugget mode) */
  #bundlePrice = null;

  /** @type {boolean} Whether the nugget bundle option is currently selected */
  #nuggetBundleSelected = false;

  connectedCallback() {
    super.connectedCallback();
    this.#observeBuyBox();
    this.#syncFrequency();

    // Listen for subscription/one-time toggle + frequency changes
    document.addEventListener("selling-plan:update", this.#onSellingPlanUpdate);

    // Hide permanently once a product is added (sticky footer takes over)
    document.addEventListener("cart:update", this.#onCartUpdate);

    this.#variant = JSON.parse(this.dataset.variant || "null");

    // Nugget mode: fetch bundle variant price in background
    if (this.dataset.nuggetMode === "true") {
      this.#nuggetBundleSelected = true; // Bundle & Save is default in nugget mode
      const bundleVariantId = this.dataset.bundleVariantId;
      if (bundleVariantId) {
        fetch(`/variants/${bundleVariantId}.json`)
          .then((res) => res.json())
          .then((data) => {
            this.#bundlePrice = data?.variant?.price ?? 7900;
            // Refresh displayed price if bundle is still selected
            if (this.#nuggetBundleSelected) {
              this.#updatePrice(this.#bundlePrice);
            }
          })
          .catch(() => {
            // Fallback to hardcoded $79 (matches gr-nugget-options.liquid:19)
            this.#bundlePrice = 7900;
          });
      } else {
        this.#bundlePrice = 7900;
      }
    }

    // Set initial price from the currently selected selling plan
    this.#syncInitialPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    document.removeEventListener(
      "selling-plan:update",
      this.#onSellingPlanUpdate,
    );
    document.removeEventListener("cart:update", this.#onCartUpdate);
  }

  /**
   * Watch gr-product-options — show sticky bar only when it scrolls completely
   * above the viewport
   */
  #observeBuyBox() {
    const productOptions = document.querySelector("gr-product-options");

    if (!productOptions) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.#hide();
        } else {
          // Only show when the element is above the viewport (scrolled past it)
          const isAboveViewport = entry.boundingClientRect.bottom < 0;
          if (isAboveViewport) {
            this.#show();
          } else {
            this.#hide();
          }
        }
      },
      { threshold: 0 },
    );

    this.#observer.observe(productOptions);

    // Start hidden
    this.#hide();
  }

  /**
   * Delegate click to the existing product form's submit button.
   */
  handleCTA() {
    // Nugget mode with bundle selected → navigate to bundle flow URL
    if (this.dataset.nuggetMode === "true" && this.#nuggetBundleSelected) {
      const url = this.dataset.bundleFlowUrl;
      if (url) {
        window.location.href = url;
        return;
      }
    }

    const form = document.querySelector("product-form-component form");
    if (!form) return;

    const submitButton = /** @type {HTMLButtonElement | null} */ (
      form.querySelector("button[name='add']")
    );

    if (submitButton && !submitButton.disabled) {
      submitButton.click();
    }
  }

  /**
   * Scroll back up to the product options section.
   */
  handleFrequencyClick() {
    const productOptions = document.querySelector("gr-product-options");
    if (productOptions) {
      productOptions.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /**
   * Set the initial price based on whatever selling plan is selected on load.
   */
  #syncInitialPrice() {
    // Nugget mode: use bundle price (default selection) or wait for fetch
    if (this.dataset.nuggetMode === "true") {
      this.#updatePrice(this.#bundlePrice ?? 7900);
      this.#setFrequencyText("bundle & save");
      return;
    }

    if (!this.#frequencySelect) {
      // No frequency select = one-time only product
      this.#setFrequencyText(null);
      this.#updatePrice(this.#variant?.price);
      return;
    }

    const selectedOption = this.#frequencySelect.selectedOptions[0];
    const sellingPlanId = selectedOption?.dataset.sellingPlanId || null;
    this.#updatePrice(this.#getSellingPlanPrice(sellingPlanId));
  }

  /**
   * Read the initial frequency from the select on the page.
   */
  #syncFrequency() {
    this.#frequencySelect = /** @type {HTMLSelectElement | null} */ (
      document.querySelector("gr-product-options [ref='frequencySelect']")
    );

    if (!this.#frequencySelect) return;

    const text = this.#frequencySelect.selectedOptions[0]?.textContent?.trim();
    this.#setFrequencyText(text);
  }

  /**
   * Handle selling-plan:update events from gr-product-options.
   * Covers both subscription/one-time toggle and frequency dropdown changes.
   * @param {Event} e
   */
  #onSellingPlanUpdate = (e) => {
    const { type, frequency, sellingPlanId } = /** @type {any} */ (e).detail;

    if (this.dataset.nuggetMode === "true") {
      if (type === "one-time") {
        this.#nuggetBundleSelected = false;
        this.#setFrequencyText(null);
        const oneTimePrice = parseInt(this.dataset.oneTimePrice || "0", 10);
        this.#updatePrice(oneTimePrice);
      } else {
        this.#nuggetBundleSelected = true;
        this.#setFrequencyText("bundle & save");
        this.#updatePrice(this.#bundlePrice ?? 7900);
      }
      return;
    }

    if (type === "one-time") {
      this.#setFrequencyText(null);
      this.#updatePrice(this.#variant?.price);
    } else {
      this.#setFrequencyText(frequency);
      this.#updatePrice(this.#getSellingPlanPrice(sellingPlanId));
    }
  };

  /**
   * Find the subscription price for a given selling plan ID.
   * Falls back to the base variant price if no match.
   * @param {string | null} sellingPlanId
   * @returns {number}
   */
  #getSellingPlanPrice(sellingPlanId) {
    if (!this.#variant || !sellingPlanId) return this.#variant?.price ?? 0;

    const allocation = this.#variant.selling_plan_allocations?.find(
      (/** @type {any} */ a) =>
        String(a.selling_plan_id) === String(sellingPlanId),
    );

    return allocation?.price ?? this.#variant.price;
  }

  /**
   * Format cents and update the price display.
   * @param {number | undefined} cents
   */
  #updatePrice(cents) {
    const el = /** @type {HTMLElement | null} */ (this.refs.priceText);
    if (!el || cents == null) return;

    el.textContent = `$${(cents / 100).toFixed(0)}`;
  }

  /** @param {string | null | undefined} text */
  #setFrequencyText(text) {
    const el = /** @type {HTMLElement | null} */ (this.refs.frequencyText);
    if (!el) return;

    // Nugget mode uses "bundle & save" / "one time" without "every" prefix
    if (this.dataset.nuggetMode === "true") {
      el.textContent = text || "one time";
      return;
    }

    el.textContent = text ? `every ${text}` : "one time";
  }

  /**
   * Hide sticky ATC permanently once a product is added from this page.
   * The gr-pdp-sticky-footer takes over after that.
   */
  #onCartUpdate = (/** @type {any} */ e) => {
    const source = e.detail?.data?.source;
    if (source !== "product-form-component") return;
    if (e.detail?.data?.didError) return;

    this.#productAdded = true;
    this.#hide();
  };

  #show() {
    if (this.#productAdded) return;
    this.removeAttribute("hidden");
  }

  #hide() {
    this.setAttribute("hidden", "");
  }
}

if (!customElements.get("gr-sticky-add-to-cart")) {
  customElements.define("gr-sticky-add-to-cart", GRStickyAddToCart);
}
